class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      if (facetForm.id === 'FacetFiltersFormMobile' && window.innerWidth <= 749) return;
      this.debouncedOnSubmit(event);
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
        bsSyncStockToggle();
    bsSyncPricePresets();
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    const collectionTitleCount = document.querySelector('[data-bs-collection-count]');
    if (collectionTitleCount && sourceCount.dataset.productCount !== undefined) {
      collectionTitleCount.textContent = `（${sourceCount.dataset.productCount}個の商品）`;
    }
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
    bsInitializeFacetTabs();
    bsInitializeMobileFilterDraft();
    bsSyncStockToggle();
    bsSyncPricePresets();
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const submittedForm = event.target.closest('form');

    if (submittedForm?.id === 'FacetSortMobileForm') {
      const selectedSort = submittedForm.querySelector('input[name="sort_by"]:checked');
      bsRestoreMobileFilterValues();
      const mobileFilterForm = document.getElementById('FacetFiltersFormMobile');
      const mobileSortInput = mobileFilterForm?.querySelector('[data-mobile-sort-value]');

      if (selectedSort && mobileSortInput) {
        mobileSortInput.value = selectedSort.value;
      }

      submittedForm.closest('.bs-mobile-sort-sheet')?.removeAttribute('open');
      const searchParams = this.createSearchParams(mobileFilterForm || submittedForm);
      this.onSubmitForm(searchParams, event);
      return;
    }

    if (submittedForm?.id === 'FacetFiltersFormMobile' && window.innerWidth <= 749) return;
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = submittedForm.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event.target.closest('.bs-category-facet') ? null : event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

function bsSyncStockToggle() {
  const form = document.getElementById('FacetFiltersFormMobile');
  document.querySelectorAll('[data-bs-stock-toggle], [data-bs-domestic-stock]').forEach((button) => {
    const input = form && Array.from(form.querySelectorAll('input[type="checkbox"]')).find(
      (item) => item.name === button.dataset.stockParam && item.value === button.dataset.stockValue,
    );
    if (!input) return;
    button.setAttribute('aria-pressed', String(input.checked));
    button.removeAttribute('aria-busy');
    button.disabled = input.disabled && !button.hasAttribute('data-bs-domestic-stock');
  });
}

function bsInitializeFacetTabs() {
  const form = document.getElementById('FacetFiltersFormMobile');
  const main = form?.querySelector('.mobile-facets__main');
  if (!main) return;
  const panels = Array.from(main.children).filter((element) => element.matches('details.mobile-facets__details'));
  const mobile = window.matchMedia('(max-width: 749px)').matches;
  if (!mobile) {
    form.classList.remove('bs-facet-tabs-ready');
    panels.forEach((panel) => { panel.hidden = false; });
    return;
  }

  let tabs = form.querySelector('.bs-facet-tabs');
  if (!tabs) {
    tabs = document.createElement('div');
    tabs.className = 'bs-facet-tabs';
    tabs.setAttribute('role', 'tablist');
    tabs.setAttribute('aria-label', '絞り込み項目');
    main.before(tabs);
    tabs.addEventListener('click', (event) => {
      const button = event.target.closest('[role="tab"], [data-bs-domestic-stock]');
      if (!button || button.disabled) return;
      event.stopPropagation();
      if (button.hasAttribute('data-bs-domestic-stock')) {
        bsApplyDomesticStockFromDrawer(button);
        return;
      }
      select(button.dataset.panelId);
    }, true);
    tabs.addEventListener('keydown', (event) => {
      const buttons = Array.from(tabs.querySelectorAll('[role="tab"]'));
      const current = buttons.indexOf(event.target);
      if (current < 0) return;
      let next;
      if (event.key === 'ArrowRight') next = (current + 1) % buttons.length;
      if (event.key === 'ArrowLeft') next = (current + buttons.length - 1) % buttons.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = buttons.length - 1;
      if (next === undefined) return;
      event.preventDefault();
      select(buttons[next].dataset.panelId);
      buttons[next].focus();
    });
  }

  function select(id) {
    form.dataset.bsCurrentFacet = id;
    Array.from(main.children).filter((element) => element.matches('details.mobile-facets__details')).forEach((panel) => {
      const active = panel.id === id;
      panel.hidden = !active;
      panel.open = active;
    });
    tabs.querySelectorAll('[role="tab"]').forEach((button) => {
      const active = button.dataset.panelId === id;
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
    });
    main.scrollTop = 0;
  }

  const signature = panels.map((panel, index) => {
    if (!panel.id) panel.id = `BsFacetCategoryPanel-${index}`;
    return panel.id;
  }).join('|');
  if (tabs.dataset.signature !== signature) {
    tabs.replaceChildren();
    panels.forEach((panel) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.id = `${panel.id}-tab`;
      const stock = panel.querySelector('input[name="filter.p.m.custom.domestic_in_stock"]');
      if (stock) {
        button.dataset.bsDomesticStock = 'true';
        button.dataset.stockParam = stock.name;
        button.dataset.stockValue = stock.value;
        button.setAttribute('aria-description', '国内在庫ありのみを適用して一覧へ戻る');
      } else {
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', panel.id);
      }
      button.dataset.panelId = panel.id;
      button.textContent = panel.querySelector(':scope > summary').textContent.trim().replace('国内在庫状況', '国内在庫');
      tabs.append(button);
    });
    tabs.dataset.signature = signature;
  }
  panels.forEach((panel) => {
    panel.classList.add('bs-facet-panel');
    if (panel.querySelector('input[name="filter.p.m.custom.domestic_in_stock"]')) {
      panel.removeAttribute('role');
      panel.removeAttribute('aria-labelledby');
      return;
    }
    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('aria-labelledby', `${panel.id}-tab`);
  });
  form.classList.add('bs-facet-tabs-ready');
  const selectablePanels = panels.filter((panel) => !panel.querySelector('input[name="filter.p.m.custom.domestic_in_stock"]'));
  select(selectablePanels.some((panel) => panel.id === form.dataset.bsCurrentFacet) ? form.dataset.bsCurrentFacet : selectablePanels[0]?.id);
  bsSyncStockToggle();
    bsSyncPricePresets();
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-bs-stock-toggle]');
  if (!button || button.disabled) return;
  bsRestoreMobileFilterValues();
  const form = document.getElementById('FacetFiltersFormMobile');
  const input = form && Array.from(form.querySelectorAll('input[type="checkbox"]')).find(
    (item) => item.name === button.dataset.stockParam && item.value === button.dataset.stockValue,
  );
  if (!input) return;
  input.checked = !input.checked;
  button.setAttribute('aria-pressed', String(input.checked));
  button.setAttribute('aria-busy', 'true');
  button.disabled = true;
  FacetFiltersForm.renderPage(new URLSearchParams(new FormData(form)).toString());
});
window.addEventListener('resize', bsInitializeFacetTabs);
bsInitializeFacetTabs();

function bsRestoreMobileFilterValues() {
  const form = document.getElementById('FacetFiltersFormMobile');
  if (!form) return;
  const params = new URLSearchParams(window.location.search);
  form.querySelectorAll('input').forEach((input) => {
    if (!input.name.startsWith('filter.')) return;
    const values = params.getAll(input.name).flatMap((value) => value.split(','));
    if (input.type === 'checkbox') input.checked = values.includes(input.value);
    else input.value = params.get(input.name) || '';
  });
  bsSyncStockToggle();
    bsSyncPricePresets();
}

function bsInitializeMobileFilterDraft() {
  const form = document.getElementById('FacetFiltersFormMobile');
  const drawer = form?.closest('.mobile-facets__wrapper')?.querySelector(':scope > details');
  if (!drawer || drawer.dataset.bsDraftReady) return;
  drawer.dataset.bsDraftReady = 'true';
  drawer.addEventListener('toggle', () => {
    if (drawer.open) bsRestoreMobileFilterValues();
  });
}

document.addEventListener('click', (event) => {
  const apply = event.target.closest('[data-bs-facet-apply]');
  const clear = event.target.closest('[data-bs-facet-clear]');
  if (!apply && !clear) return;
  const form = event.target.closest('form');
  if (!form) return;
  event.preventDefault();
  event.stopPropagation();
  if (clear) {
    form.querySelectorAll('input').forEach((input) => {
      if (!input.name.startsWith('filter.')) return;
      if (input.type === 'checkbox') input.checked = false;
      else input.value = '';
    });
    bsSyncPricePresets();
    return;
  }
  form.closest('.mobile-facets__wrapper').querySelector(':scope > details > summary').click();
  FacetFiltersForm.renderPage(new URLSearchParams(new FormData(form)).toString());
}, true);
bsInitializeMobileFilterDraft();

function bsSyncPricePresets() {
  const form = document.getElementById('FacetFiltersFormMobile');
  if (!form) return;
  const min = form.querySelector('input[name="filter.v.price.gte"]')?.value.replace(/,/g, '') || '';
  const max = form.querySelector('input[name="filter.v.price.lte"]')?.value.replace(/,/g, '') || '';
  form.querySelectorAll('[data-bs-price-min]').forEach((button) => {
    button.setAttribute('aria-pressed', String(button.dataset.bsPriceMin === min && button.dataset.bsPriceMax === max));
  });
  const output = form.querySelector('[data-bs-price-current]');
  if (output) {
    const format = (value) => Number(value).toLocaleString('ja-JP');
    output.textContent = !min && !max ? '価格の指定なし' : `選択中：${min ? format(min) : '0'}円〜${max ? `${format(max)}円` : ''}`;
  }
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-bs-price-min]');
  if (!button) return;
  const form = button.closest('form');
  const min = form?.querySelector('input[name="filter.v.price.gte"]');
  const max = form?.querySelector('input[name="filter.v.price.lte"]');
  if (!min || !max) return;
  event.preventDefault();
  event.stopPropagation();
  min.value = button.dataset.bsPriceMin;
  max.value = button.dataset.bsPriceMax;
  bsSyncPricePresets();
}, true);
bsSyncPricePresets();

function bsApplyDomesticStockFromDrawer(button) {
  const form = document.getElementById('FacetFiltersFormMobile');
  const input = form && Array.from(form.querySelectorAll('input[type="checkbox"]')).find(
    (item) => item.name === button.dataset.stockParam && item.value === button.dataset.stockValue,
  );
  if (!input) return;
  input.checked = true;
  const params = new URLSearchParams(new FormData(form));
  params.set(input.name, input.value);
  button.disabled = true;
  button.setAttribute('aria-pressed', 'true');
  button.setAttribute('aria-busy', 'true');
  form.closest('.mobile-facets__wrapper').querySelector(':scope > details > summary').click();
  FacetFiltersForm.renderPage(params.toString());
}
